import React, { useState, useEffect } from "react";
import { X, LogOut, Calendar, ShoppingBag, Users, FileText, Settings, QrCode, ImageIcon, Edit2, Save, Trash2, RotateCcw, AlertTriangle, BarChart3, Mail, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { AdminContentManager } from "./AdminContentManager";
import { AdminSiteSettings } from "./AdminSiteSettings";
import { AdminBackgroundManager } from "./AdminBackgroundManager";
import { AdminAnalyticsTab } from "./AdminAnalyticsTab";
import { AdminReminders } from "./AdminReminders";
import { AdminEventEditor } from "./AdminEventEditor";
import { TicketScanner } from "./TicketScanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = ({ isOpen, onClose, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("events");
  const [editingStock, setEditingStock] = useState(null);
  const [stockValue, setStockValue] = useState("");
  const [editingFee, setEditingFee] = useState(null);
  const [feeValue, setFeeValue] = useState("");
  const [editingEvent, setEditingEvent] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [resending, setResending] = useState(null);
  const [selectedEventFilter, setSelectedEventFilter] = useState(null);
  const [stats, setStats] = useState({ total_revenue: 0, total_orders: 0, total_tickets: 0 });

  const paidOrders = orders.filter(o => 
    o.payment_status === "paid" || o.payment_status === "completed"
  );

  const filteredOrders = selectedEventFilter
    ? paidOrders.filter(o => (o.items || []).some(i => i.event_id === selectedEventFilter))
    : paidOrders;

  const totalRevenue = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);

  useEffect(() => {
    if (isOpen) {
      fetchAdminData();
    }
  }, [isOpen]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_admin_token");
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      // Fetch data in parallel for faster loading
      const [eventsRes, ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/events?limit=100`, { headers }),
        axios.get(`${API}/admin/orders?limit=200`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }).catch(() => ({ data: {} }))
      ]);

      setEvents(eventsRes.data || []);
      setOrders(ordersRes.data || []);
      setStats(statsRes.data || { total_revenue: 0, total_orders: 0, total_tickets: 0 });
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet événement ?")) {
      return;
    }

    try {
      const token = localStorage.getItem("tika_admin_token");
      await axios.delete(`${API}/admin/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Événement supprimé");
      fetchAdminData();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    
    try {
      const token = localStorage.getItem("tika_admin_token");
      await axios.delete(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Commande supprimée");
      fetchAdminData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleResetStats = async () => {
    try {
      const token = localStorage.getItem("tika_admin_token");
      await axios.post(`${API}/admin/reset-stats`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Statistiques remises à zéro");
      setShowResetConfirm(false);
      fetchAdminData();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    }
  };

  const handleUpdateStock = async (eventId) => {
    try {
      const token = localStorage.getItem("tika_admin_token");
      const newStock = parseInt(stockValue) || 0;
      
      await axios.put(`${API}/admin/events/${eventId}/stock`, 
        { available_tickets: newStock },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Stock mis à jour");
      setEditingStock(null);
      setStockValue("");
      fetchAdminData();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la mise à jour du stock");
    }
  };

  const handleUpdateFee = async (eventId) => {
    try {
      const token = localStorage.getItem("tika_admin_token");
      const newFee = parseFloat(feeValue) || 0;
      
      await axios.put(`${API}/admin/events/${eventId}/service-fee`, 
        { service_fee: newFee },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Frais de service mis à jour");
      setEditingFee(null);
      setFeeValue("");
      fetchAdminData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleResendTicket = async (orderId) => {
    setResending(orderId);
    try {
      const token = localStorage.getItem("tika_admin_token");
      const res = await axios.post(`${API}/admin/resend-ticket/${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
    } catch (error) {
      toast.error("Erreur lors du renvoi");
    } finally {
      setResending(null);
    }
  };

  const handleSearchOrders = async () => {
    if (!orderSearch.trim()) {
      fetchAdminData();
      return;
    }
    try {
      const token = localStorage.getItem("tika_admin_token");
      const res = await axios.get(`${API}/admin/orders?search=${encodeURIComponent(orderSearch)}&limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data || []);
    } catch (error) {
      toast.error("Erreur de recherche");
    }
  };

  const handleDeletePending = async () => {
    try {
      const token = localStorage.getItem("tika_admin_token");
      const res = await axios.delete(`${API}/admin/orders/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchAdminData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("tika_admin_token");
    onLogout();
    toast.success("Déconnexion réussie");
  };

  let totalTickets = 0;
  for (let i = 0; i < orders.length; i++) {
    const orderItems = orders[i].items || [];
    for (let j = 0; j < orderItems.length; j++) {
      totalTickets += orderItems[j].quantity || 0;
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl text-street tracking-wide">
              Tableau de bord Admin
            </DialogTitle>
            <Button
              data-testid="admin-logout-btn"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Chargement...</div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="glass-card rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{events.length}</div>
                    <div className="text-xs text-muted-foreground">Événements</div>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{orders.length}</div>
                    <div className="text-xs text-muted-foreground">Commandes</div>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{Math.floor(totalRevenue)}€</div>
                    <div className="text-xs text-muted-foreground">Revenus</div>
                  </div>
                </div>
              </div>
            </div>

            <Tabs defaultValue="events" value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full mb-4 grid grid-cols-7">
                <TabsTrigger value="events" className="flex-1 text-xs sm:text-sm">
                  <span className="hidden sm:inline">Événements</span>
                  <span className="sm:hidden">Évén.</span>
                </TabsTrigger>
                <TabsTrigger value="orders" className="flex-1 text-xs sm:text-sm">
                  <span className="hidden sm:inline">Commandes</span>
                  <span className="sm:hidden">Cmd.</span>
                </TabsTrigger>
                <TabsTrigger value="analytics" className="flex-1 text-xs sm:text-sm" data-testid="admin-analytics-tab-trigger">
                  <BarChart3 className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Analytics</span>
                </TabsTrigger>
                <TabsTrigger value="scanner" className="flex-1 text-xs sm:text-sm" data-testid="scanner-tab">
                  <QrCode className="w-4 h-4" />
                </TabsTrigger>
                <TabsTrigger value="content" className="flex-1 text-xs sm:text-sm">
                  <FileText className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Contenu</span>
                </TabsTrigger>
                <TabsTrigger value="background" className="flex-1 text-xs sm:text-sm" data-testid="background-tab">
                  <ImageIcon className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Fond</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 text-xs sm:text-sm">
                  <Settings className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Param.</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="events" className="space-y-3">
                {events.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">Aucun événement</div>
                ) : (
                  <div className="space-y-3">
                    {events.map((event) => (
                      <div key={event.id} data-testid={`admin-event-${event.id}`} className="glass-card rounded-lg p-4">
                        {/* Header row: info + action buttons */}
                        <div className="flex items-start gap-3 mb-2">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm truncate">{event.title}</h4>
                            <p className="text-xs text-muted-foreground">{event.artist} • {event.date}</p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingEvent(editingEvent === event.id ? null : event.id)}
                              className={`h-8 px-2 ${editingEvent === event.id ? "border-primary text-primary" : ""}`}
                              data-testid={`edit-event-${event.id}`}
                            >
                              <Edit2 className="w-3 h-3 mr-1" />
                              <span className="text-xs">Modifier</span>
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteEvent(event.id)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Badges: category, price, stock, fee */}
                        <div className="flex flex-wrap gap-2 text-xs items-center">
                          <span className="px-2 py-1 bg-primary/20 text-primary rounded">{event.category}</span>
                          <span className="px-2 py-1 bg-muted/30 rounded">{event.price}€</span>
                          
                          {editingStock === event.id ? (
                            <div className="flex items-center gap-1">
                              <Input type="number" value={stockValue} onChange={(e) => setStockValue(e.target.value)} className="w-20 h-7 text-xs" min="0" autoFocus />
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleUpdateStock(event.id)}><Save className="w-3 h-3" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingStock(null); setStockValue(""); }}><X className="w-3 h-3" /></Button>
                            </div>
                          ) : (
                            <span
                              className={`px-2 py-1 rounded cursor-pointer flex items-center gap-1 ${(event.available_tickets || 0) === 0 ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}`}
                              onClick={() => { setEditingStock(event.id); setStockValue((event.available_tickets || 0).toString()); }}
                            >
                              {event.available_tickets || 0} billets <Edit2 className="w-3 h-3" />
                            </span>
                          )}

                          {event.sold_out && <span className="px-2 py-1 bg-red-500/20 text-red-500 rounded font-semibold">COMPLET</span>}

                          {editingFee === event.id ? (
                            <div className="flex items-center gap-1">
                              <Input type="number" value={feeValue} onChange={(e) => setFeeValue(e.target.value)} className="w-20 h-7 text-xs" min="0" step="0.01" autoFocus />
                              <span className="text-xs">€</span>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleUpdateFee(event.id)}><Save className="w-3 h-3" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingFee(null); setFeeValue(""); }}><X className="w-3 h-3" /></Button>
                            </div>
                          ) : (
                            <span
                              className="px-2 py-1 bg-muted/30 rounded cursor-pointer flex items-center gap-1"
                              onClick={() => { setEditingFee(event.id); setFeeValue((event.service_fee !== undefined ? event.service_fee : 0.99).toString()); }}
                            >
                              Frais: {event.service_fee !== undefined ? event.service_fee : 0.99}€ <Edit2 className="w-3 h-3" />
                            </span>
                          )}
                        </div>

                        {/* Editor form - appears when edit button clicked */}
                        {editingEvent === event.id && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <AdminEventEditor
                              event={event}
                              onClose={() => setEditingEvent(null)}
                              onSaved={() => { setEditingEvent(null); fetchAdminData(); }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="orders" className="space-y-3">
                {/* Event Selector */}
                <div className="glass-card rounded-lg p-4 mb-3">
                  <h3 className="font-semibold text-sm mb-3">Sélectionner un événement</h3>
                  <div className="grid grid-cols-1 gap-2">
                    <div
                      className={`p-2 rounded cursor-pointer text-sm transition-colors ${!selectedEventFilter ? 'bg-primary/20 text-primary font-medium' : 'bg-muted/20 hover:bg-muted/40'}`}
                      onClick={() => setSelectedEventFilter(null)}
                    >
                      Toutes les commandes ({orders.length})
                    </div>
                    {events.map((ev) => {
                      const count = orders.filter(o => (o.items || []).some(i => i.event_id === ev.id)).length;
                      return (
                        <div
                          key={ev.id}
                          className={`p-2 rounded cursor-pointer text-sm transition-colors flex justify-between ${selectedEventFilter === ev.id ? 'bg-primary/20 text-primary font-medium' : 'bg-muted/20 hover:bg-muted/40'}`}
                          onClick={() => setSelectedEventFilter(ev.id)}
                          data-testid={`filter-event-${ev.id}`}
                        >
                          <span className="truncate">{ev.title}</span>
                          <span className="text-xs text-muted-foreground ml-2">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Stats */}
                <div className="glass-card rounded-lg p-4 mb-3">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm">
                      {selectedEventFilter ? events.find(e => e.id === selectedEventFilter)?.title || "Événement" : "Statistiques globales"}
                    </h3>
                    <div className="flex gap-2">
                      {orders.length > paidOrders.length && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-yellow-500 border-yellow-500/30 hover:bg-yellow-500/10"
                          onClick={handleDeletePending}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Supprimer pending ({orders.length - paidOrders.length})
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                        onClick={() => setShowResetConfirm(true)}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Remettre à 0
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <div className="text-lg font-bold text-primary">{filteredOrders.length}</div>
                      <div className="text-xs text-muted-foreground">Commandes</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-primary">
                        {filteredOrders.reduce((sum, o) => sum + (o.items || []).reduce((s, i) => s + (i.quantity || 0), 0), 0)}
                      </div>
                      <div className="text-xs text-muted-foreground">Billets</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-primary">
                        {filteredOrders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(0)}€
                      </div>
                      <div className="text-xs text-muted-foreground">Revenus</div>
                    </div>
                  </div>
                </div>

                {/* Reset Confirmation Modal */}
                {showResetConfirm && (
                  <div className="glass-card rounded-lg p-4 border-2 border-red-500/30 mb-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-bold text-red-500 mb-1">Remettre les stats à zéro ?</h4>
                        <p className="text-xs text-muted-foreground mb-3">
                          Cette action supprimera TOUTES les commandes et réinitialisera les revenus. Action irréversible.
                        </p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => setShowResetConfirm(false)} className="flex-1">
                            Annuler
                          </Button>
                          <Button size="sm" className="flex-1 bg-red-500 hover:bg-red-600" onClick={handleResetStats}>
                            Confirmer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search Bar */}
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Rechercher par nom, email ou ID..."
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearchOrders()}
                    className="flex-1"
                    data-testid="order-search-input"
                  />
                  <Button variant="outline" size="icon" onClick={handleSearchOrders}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>

                {/* Orders List */}
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Aucune commande {selectedEventFilter ? "pour cet événement" : ""}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredOrders.map((order) => {
                      const items = order.items || [];
                      const ticketCount = items.reduce((s, i) => s + (i.quantity || 0), 0);
                      
                      return (
                        <div
                          key={order.id}
                          data-testid={`admin-order-${order.id}`}
                          className="glass-card rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-sm">{order.customer_name}</h4>
                              <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                              {order.customer_phone && <p className="text-xs text-muted-foreground">{order.customer_phone}</p>}
                              <p className="text-xs text-muted-foreground font-mono">#{order.id?.slice(0, 8)}</p>
                            </div>
                            <div className="text-right flex items-start gap-2">
                              <div>
                                <div className="font-bold text-primary">{(order.total || 0).toFixed(2)}€</div>
                                <div className="text-xs text-muted-foreground">{ticketCount} billet{ticketCount > 1 ? 's' : ''}</div>
                              </div>
                              <Button 
                                variant="ghost" size="icon" 
                                className="h-8 w-8 text-red-500 hover:bg-red-500/10"
                                onClick={() => handleDeleteOrder(order.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              order.payment_status === "paid" || order.payment_status === "completed" 
                                ? "bg-green-500/20 text-green-400" 
                                : "bg-yellow-500/20 text-yellow-400"
                            }`}>
                              {order.payment_status || order.status || "pending"}
                            </span>
                            <Button
                              variant="outline" size="sm" className="h-6 text-xs"
                              onClick={() => handleResendTicket(order.id)}
                              disabled={resending === order.id}
                              data-testid={`resend-ticket-${order.id}`}
                            >
                              <Mail className="w-3 h-3 mr-1" />
                              {resending === order.id ? "Envoi..." : "Renvoyer billet"}
                            </Button>
                          </div>
                          <div className="space-y-1">
                            {items.map((item, idx) => (
                              <div key={idx} className="text-xs text-muted-foreground flex justify-between">
                                <span>{item.event_title}</span>
                                <span>{item.ticket_type} × {item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Analytics Tab */}
              <TabsContent value="analytics">
                <AdminAnalyticsTab />
              </TabsContent>

              {/* Scanner Tab */}
              <TabsContent value="scanner">
                <TicketScanner />
              </TabsContent>

              {/* Content Management Tab */}
              <TabsContent value="content">
                <AdminContentManager />
              </TabsContent>

              {/* Background Images Tab */}
              <TabsContent value="background">
                <AdminBackgroundManager />
              </TabsContent>

              {/* Settings Tab */}
              <TabsContent value="settings">
                <div className="space-y-6">
                  <AdminReminders />
                  <div className="border-t border-border pt-4">
                    <AdminSiteSettings />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
